import { ReactElement, useContext, useRef } from "react";
import { Button, IconButton, List, ListItem, ListItemText } from "@mui/material";
import { ITaskAttachment } from "../types";
import { Add, Download } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import JSZip from "jszip";
import { addAlert } from "../../../../actions";
import { useAsyncFetch } from "../../../../system/useFetch";
import { IEditionResponse } from "../../../../types";
import { useDispatch } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import { SystemContext } from "../../../../context/systemContext";


interface IProps {
  loading: boolean;
  attachments: ITaskAttachment[];
  setAttachments: (attachments: ITaskAttachment[]) => void;
}

const readFiles = async (files: any[]): Promise<ITaskAttachment[]> => {
  const reader = new FileReader();
  const result: ITaskAttachment[] = [];


  const readFile = (file: any): Promise<any> => {

    return new Promise((resolve, reject) => {
      reader.onloadend = (event) => {
        resolve({
          taskID: 0,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          data: event.target?.result as string
        });
      };

      try {
        reader.readAsDataURL(file);
      } catch (error) {
        console.log(error);
        reject();
      }
    });
  };

  for (const file of files) {
    result.push(await readFile(file));
  }

  return result;
};

const TaskAttach = ({ attachments, setAttachments, loading }: IProps): ReactElement => {
  const dispatch = useDispatch<any>();
  const systemContext = useContext(SystemContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const [executeRequest] = useAsyncFetch<IEditionResponse>();


  const listener = (e: any) => {
    readFiles(e.target.files).then(files => {
      setAttachments([...attachments, ...files.filter(x => attachments.find(y => y.fileName === x.fileName) === undefined)]);

      if (inputRef.current) {
        inputRef.current.removeEventListener("change", listener);
      }
    });
  };

  const onFilesSelected = () => {
    if (inputRef.current) {
      inputRef.current.accept = "*";
      inputRef.current.multiple = true;
      inputRef.current.addEventListener("change", listener);
      inputRef.current.click();
    }
  };

  const onDownload = () => {
    const zip = new JSZip();

    for (const item of attachments) {
      const base64result = item.data.split(",")[1];
      zip.file(item.fileName, base64result, { base64: true });
    }

    zip.generateAsync({ type: "base64" }).then(base64 => {
      const elem = window.document.createElement("a");
      elem.href = "data:application/zip;base64," + base64;
      elem.download = "files.zip";
      elem.style.display = "none";
      (document.body || document.documentElement).appendChild(elem);
      if (typeof elem.click === "function") {
        elem.click();
      } else {
        elem.target = "_blank";
        elem.dispatchEvent(new MouseEvent("click", {
          view: window,
          bubbles: true,
          cancelable: true
        }));
      }

      URL.revokeObjectURL(elem.href);
      elem.remove();
    });
  };

  const onDeleteAttachment = (item: ITaskAttachment) => {
    systemContext.showMessage({
      title: "Delete Attachment",
      color: "danger",
      message: `Do you want to delete the file "${item.fileName}"?`,
      callback: () => {
        if (item.taskAttachmentID) {
          executeRequest({
            module: "task",
            query: "delAttachment",
            payload: { taskAttachmentID: item.taskAttachmentID }
          })
            .then(response => {
              if (response?.result) {
                dispatch(addAlert({ message: "The file was deleted!", severity: "error", hash: undefined }));
              }
            });
        }

        setAttachments(attachments.filter(x => x.fileName !== item.fileName));
      }
    });
  };

  const onDownloadSingle = (item: ITaskAttachment) => {
    const elem = window.document.createElement("a");
    elem.href = item.data;
    elem.download = item.fileName;
    elem.style.display = "none";
    (document.body || document.documentElement).appendChild(elem);
    if (typeof elem.click === "function") {
      elem.click();
    } else {
      elem.target = "_blank";
      elem.dispatchEvent(new MouseEvent("click", {
        view: window,
        bubbles: true,
        cancelable: true
      }));
    }

    URL.revokeObjectURL(elem.href);
    elem.remove();
  };

  return (
    <div>
      <input type="file" hidden={true} ref={inputRef}/>
      <Button onClick={onFilesSelected} variant="contained" color="primary"
        startIcon={<Add/>} className="m-1" disabled={attachments.length > 4}>
        Add Files
      </Button>
      <Button onClick={onDownload} variant="contained" color="info"
        startIcon={<Download/>} className="m-1" disabled={attachments.length === 0}>
        Download Files
      </Button>
      { loading && <div className="text-center"><CircularProgress /></div> }
      {!loading && <List dense={true} component="nav">
        {attachments.map((item, index) => {
          return (
            <ListItem key={`attach-${index}`} secondaryAction={
              <>
                <IconButton edge="end" aria-label="download" color="info" onClick={() => onDownloadSingle(item)}>
                  <Download/>
                </IconButton>
                <IconButton edge="end" aria-label="delete" color="error" onClick={() => onDeleteAttachment(item)}>
                  <DeleteIcon/>
                </IconButton>
              </>
            }>
              <ListItemText primary={item.fileName} secondary={item.fileType}/>
            </ListItem>
          );
        })}
      </List>
      }
    </div>
  );
};

export default TaskAttach;
